import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Gift } from 'lucide-react';

const BonusCard = ({ title, subtitle, description, value, image, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: 0.1 + index * 0.15, duration: 0.6 }}
      className="group"
    >
      <div className="luxury-card-gold rounded-3xl overflow-hidden h-full hover:border-accent/50 transition-all duration-500">
        {/* Image */}
        <div className="relative h-56 overflow-hidden">
          <img
            src={image}
            alt={title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-card via-card/60 to-transparent" />
          
          {/* Value badge */}
          <div className="absolute top-4 right-4">
            <span className="px-4 py-2 rounded-full bg-accent text-background font-body text-sm font-semibold">
              Valeur : {value}
            </span>
          </div>

          {/* Bonus number */}
          <div className="absolute bottom-4 left-4 flex items-center gap-2">
            <Gift className="w-5 h-5 text-accent" />
            <span className="text-accent font-display text-sm">{subtitle}</span>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 md:p-8">
          <h3 className="font-display text-xl text-foreground mb-4 leading-tight">{title}</h3>
          <p className="text-muted-foreground font-body leading-relaxed text-sm">{description}</p>
        </div>
      </div>
    </motion.div>
  );
};

export const BonusSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const bonuses = [
    {
      subtitle: "Bonus #1",
      title: "Le protocole chamanique des deuils et renaissances 🌓",
      description: "Libère l'ancien pour accueillir le nouveau grâce à ce puissant protocole qui fait appel à la puissance des esprits. Tu vas vivre une libération complète des énergies du passé, lâcher ce qui ne te sert plus, semer le renouveau et ouvrir la page de ton nouveau chapitre.",
      value: "149€",
      image: "https://gillesdelieuze.com/wp-content/uploads/2024/11/gilles1385_renaissance_new_age_galactic_divine_white_gold_multi_a29bb5e9-b1fc-47ee-9f96-bca52d8ac863.png"
    },
    {
      subtitle: "Bonus #2",
      title: "3 transmissions énergétiques avec la sagesse Baleine, Béluga et Raie",
      description: "Ces énergies te permettent d'accéder à des savoirs galactiques. Ils sont des outils puissants pour intégrer des enseignements spirituels profonds, accéder à tes archives akashiques et des mémoires ancestrales, et comprendre ta place et ton rôle dans le monde.",
      value: "231€",
      image: "https://gillesdelieuze.com/wp-content/uploads/2024/11/gilles1385_3_animals_on_a_picture_baleine_raie_et_beluga_cosmos_599d2284-e4f5-40ad-8ce2-ee6b4e23be07.png"
    },
    {
      subtitle: "Bonus #3",
      title: "Métamorphose : la méthode pour OSER prendre TA PLACE",
      description: "Je te partage une conférence inédite où je partage ce qui m'a permis de prendre ma place après des années à me chercher. Tu vas découvrir 5 clés pour suivre ta voix et 7 pépites pour lancer et faire fleurir une activité alignée avec tes valeurs.",
      value: "180€",
      image: "https://gillesdelieuze.com/wp-content/uploads/2024/04/ar6bd32l.bmp"
    }
  ];

  return (
    <section ref={ref} className="section-padding bg-background-secondary relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-accent/5 rounded-full blur-3xl" />
      </div>

      <div className="container-luxury relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="text-accent font-body text-sm tracking-[0.3em] uppercase mb-4 block">
            Et ce n'est pas tout...
          </span>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-foreground leading-tight mb-6">
            Tu auras accès à <span className="text-gradient-gold">3 bonus exclusifs</span>
          </h2>
          <div className="flex items-center justify-center gap-4 mt-6">
            <div className="h-px w-20 bg-accent/50" />
            <span className="text-muted-foreground font-body">d'une valeur totale de 560€</span>
            <div className="h-px w-20 bg-accent/50" />
          </div>
        </motion.div>

        {/* Bonus Cards */}
        <div className="grid md:grid-cols-3 gap-8">
          {bonuses.map((bonus, index) => (
            <BonusCard key={bonus.title} {...bonus} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default BonusSection;
